/**
 * Installed Plugins Tab (Ant Design v5)
 * 
 * TASK-F1.3: Complete plugin list with management actions ✅
 */

import React, { useState, useEffect } from 'react';
import {
  Input,
  Button,
  Tag,
  Avatar,
  Switch,
  Dropdown,
  Modal,
  Alert,
  Row,
  Col,
  Card,
  Divider,
  Select,
  Skeleton,
  Tooltip,
  Space,
  Typography,
  message
} from 'antd';
import {
  SearchOutlined,
  CloseOutlined,
  ReloadOutlined,
  MoreOutlined,
  SettingOutlined,
  InfoCircleOutlined,
  SyncOutlined,
  DeleteOutlined,
  EyeOutlined,
  RocketOutlined
} from '@ant-design/icons';
import DetailedPluginView from '../components/DetailedPluginView';
import { useAuthorization } from '../../../core/auth/authz-hooks';
import { useNavigate } from 'react-router-dom';
import { clearManifestsCache } from '../../../plugins/registry';

const { Text, Title, Paragraph } = Typography;

interface Plugin {
  id: string;
  name: string;
  version: string;
  description: string;
  author: string;
  icon: string;
  enabled: boolean;
  hasSettings: boolean;
  installedDate: string;
  lastUpdated: string;
  category: string;
  permissions: string[];
}

type FilterType = 'all' | 'active' | 'inactive';

const InstalledTab: React.FC = () => {
  const navigate = useNavigate();
  const [plugins, setPlugins] = useState<Plugin[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [selectedPlugin, setSelectedPlugin] = useState<Plugin | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [uninstallDialogOpen, setUninstallDialogOpen] = useState(false);
  const [pluginToUninstall, setPluginToUninstall] = useState<Plugin | null>(null);
  const [detailedViewVisible, setDetailedViewVisible] = useState(false);
  const [selectedPluginId, setSelectedPluginId] = useState<string | null>(null);
  const [pluginRoutes, setPluginRoutes] = useState<Map<string, string>>(new Map());
  
  // Check permissions for plugin management actions
  const uninstallAuth = useAuthorization('plugin', 'uninstall', true);

  // Fetch installed plugins
  useEffect(() => {
    fetchPlugins();
  }, []);

  const fetchPlugins = async () => {
    setLoading(true);
    
    try {
      // Fetch lightweight plugin manifests (fast loading for list view)
      const res = await fetch('/api/plugins/manifests', { credentials: 'include' });
      if (!res.ok) throw new Error(`Failed to load manifests: ${res.status}`);
      
      const data = await res.json();
      const manifests = Array.isArray(data?.manifests) ? data.manifests : data.manifests || [];
      
      // Build route map for plugins (for View button)
      // Note: Lightweight manifests may not have routes, so we'll load full manifest when needed
      const routeMap = new Map<string, string>();
      // For lightweight manifests, routes might not be available - will load on-demand
      manifests.forEach((manifest: any) => {
        // Check if this is a lightweight manifest (has manifest reference)
        if (manifest.manifest) {
          // Lightweight manifest - routes will be loaded when plugin is accessed
          // For now, we can try to infer route from plugin ID
          routeMap.set(manifest.id, `/${manifest.id}`);
        } else {
          // Full manifest - routes are available
          const firstRoute = manifest.contributions?.routes?.[0];
          if (firstRoute && !firstRoute.hidden) {
            routeMap.set(manifest.id, firstRoute.path);
          }
        }
      });
      setPluginRoutes(routeMap);
      
      // Transform manifest data to Plugin format
      const transformedPlugins: Plugin[] = manifests.map((manifest: any) => ({
        id: manifest.id,
        name: manifest.name || manifest.id,
        version: manifest.version || '1.0.0',
        description: manifest.description || 'No description',
        author: manifest.author || 'EmpowerID',
        icon: manifest.icon || '📦',
        enabled: manifest.enabled !== false,
        hasSettings: false, // TODO: Add settings detection
        installedDate: new Date().toISOString().split('T')[0], // Current date as fallback
        lastUpdated: new Date().toISOString().split('T')[0],
        category: manifest.group || 'General',
        // Lightweight manifests may not have permissions - will show "N/A" or load on-demand
        permissions: Array.isArray(manifest.permissions?.api) 
          ? manifest.permissions.api.map((p: any) => `${p.method}:${p.path}`) 
          : manifest.manifest ? ['Load full manifest to view'] : []
      }));
      
      setPlugins(transformedPlugins);
    } catch (error: any) {
      console.error('Error fetching plugins:', error);
      message.error(`Failed to load plugins: ${error.message}`);
      setPlugins([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter and search logic
  const filteredPlugins = plugins.filter(plugin => {
    // Apply status filter
    if (filter === 'active' && !plugin.enabled) return false;
    if (filter === 'inactive' && plugin.enabled) return false;
    
    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        plugin.name.toLowerCase().includes(query) ||
        plugin.description.toLowerCase().includes(query) ||
        plugin.author.toLowerCase().includes(query) ||
        plugin.category.toLowerCase().includes(query)
      );
    }
    
    return true;
  });

  const handleTogglePlugin = async (pluginId: string, currentState: boolean) => {
    const newState = !currentState;
    
    // Update local state optimistically
    setPlugins(prev =>
      prev.map(p =>
        p.id === pluginId ? { ...p, enabled: newState } : p
      )
    );
    
    try {
      // Call backend API to queue the plugin state change
      const endpoint = newState 
        ? `/api/plugins/enable/${pluginId}` 
        : `/api/plugins/disable/${pluginId}`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          errorData = { message: `HTTP ${response.status} ${response.statusText}` };
        }
        
        // Handle specific error cases
        if (response.status === 502) {
          const detail = errorData.detail || errorData.message || 'Service unavailable';
          throw new Error(`Backend service error: ${detail}. Please try again or contact support.`);
        }
        
        throw new Error(errorData.detail || errorData.message || `HTTP ${response.status}`);
      }
      
      const result = await response.json();
      
      message.info(
        newState 
          ? `⏳ Enabling plugin "${pluginId}"... (processing)` 
          : `⏳ Disabling plugin "${pluginId}"... (processing)`,
        1 // Short duration
      );
      
      // Poll for changes (worker processes the request asynchronously)
      // Wait a moment for worker to process, then refresh
      setTimeout(async () => {
        // Clear manifest cache so dashboard reflects changes
        clearManifestsCache();
        await fetchPlugins();
        message.success(
          newState 
            ? `✅ Plugin "${pluginId}" enabled` 
            : `⏸️ Plugin "${pluginId}" disabled`
        );
      }, 1500); // Give worker time to process
      
    } catch (error: any) {
      // Revert on error
      setPlugins(prev =>
        prev.map(p =>
          p.id === pluginId ? { ...p, enabled: currentState } : p
        )
      );
      message.error(`Failed to toggle plugin: ${error.message}`);
      console.error('Toggle plugin error:', error);
    }
  };

  const handleSettingsOpen = (plugin: Plugin) => {
    setSelectedPlugin(plugin);
    setSettingsOpen(true);
  };

  const handleSettingsClose = () => {
    setSettingsOpen(false);
    setSelectedPlugin(null);
  };

  const handleUninstallClick = (plugin: Plugin) => {
    setPluginToUninstall(plugin);
    setUninstallDialogOpen(true);
  };

  const handleUninstallConfirm = async () => {
    if (!pluginToUninstall) return;
    
    try {
      const response = await fetch(`/api/plugins/uninstall/${pluginToUninstall.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(error.detail || error.message || `HTTP ${response.status}`);
      }
      
      const result = await response.json();
      
      // Remove from local state
      setPlugins(prev => prev.filter(p => p.id !== pluginToUninstall.id));
      setUninstallDialogOpen(false);
      setPluginToUninstall(null);
      
      message.success(result.message || `Plugin "${pluginToUninstall.name}" uninstalled successfully`);
      
      // Refresh the list after a short delay to ensure backend processed it
      setTimeout(() => {
        fetchPlugins();
      }, 1000);
    } catch (error: any) {
      console.error('Uninstall error:', error);
      message.error(`Failed to uninstall plugin: ${error.message}`);
    }
  };

  const handleUninstallCancel = () => {
    setUninstallDialogOpen(false);
    setPluginToUninstall(null);
  };

  const getStatsCount = (filterType: FilterType) => {
    if (filterType === 'all') return plugins.length;
    if (filterType === 'active') return plugins.filter(p => p.enabled).length;
    if (filterType === 'inactive') return plugins.filter(p => !p.enabled).length;
    return 0;
  };

  const handleOpenPlugin = (plugin: Plugin) => {
    const route = pluginRoutes.get(plugin.id);
    if (route) {
      navigate(route);
    } else {
      message.warning(`No route found for ${plugin.name}`);
    }
  };

  const getMenuItems = (plugin: Plugin) => [
    ...(plugin.hasSettings ? [{
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => handleSettingsOpen(plugin)
    }] : []),
    {
      key: 'details',
      icon: <InfoCircleOutlined />,
      label: 'View Details',
      onClick: () => {
        setSelectedPluginId(plugin.id);
        setDetailedViewVisible(true);
      }
    },
    {
      key: 'update',
      icon: <SyncOutlined />,
      label: 'Check for Updates'
    },
    // Only show uninstall if user has permission
    ...(uninstallAuth.authorized ? [{
      type: 'divider' as const
    }, {
      key: 'uninstall',
      icon: <DeleteOutlined />,
      label: 'Uninstall',
      danger: true,
      onClick: () => handleUninstallClick(plugin)
    }] : [])
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <Title level={3}>My Apps</Title>
        <Text type="secondary">
          Manage your apps, configure settings, and control which apps are active
        </Text>
      </div>

      {/* Summary Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                  Total Installed
                </Text>
                <Title level={3} style={{ margin: 0 }}>
                  {plugins.length}
                </Title>
              </div>
              <Avatar style={{ backgroundColor: '#e6f7ff', color: '#1890ff' }}>
                📦
              </Avatar>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                  Active
                </Text>
                <Title level={3} style={{ margin: 0, color: '#52c41a' }}>
                  {plugins.filter(p => p.enabled).length}
                </Title>
              </div>
              <Avatar style={{ backgroundColor: '#f6ffed', color: '#52c41a' }}>
                ⚡
              </Avatar>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                  Inactive
                </Text>
                <Title level={3} style={{ margin: 0, color: '#8c8c8c' }}>
                  {plugins.filter(p => !p.enabled).length}
                </Title>
              </div>
              <Avatar style={{ backgroundColor: '#f5f5f5', color: '#8c8c8c' }}>
                ⏸️
              </Avatar>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Search and Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col xs={24} md={12}>
            <Input
              placeholder="Search plugins by name, description, author, or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              prefix={<SearchOutlined />}
              suffix={
                searchQuery && (
                  <CloseOutlined
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSearchQuery('')}
                  />
                )
              }
            />
          </Col>
          <Col xs={24} md={6}>
            <Select
              value={filter}
              onChange={(value) => setFilter(value)}
              style={{ width: '100%' }}
              options={[
                { label: `All Apps (${getStatsCount('all')})`, value: 'all' },
                { label: `Active Only (${getStatsCount('active')})`, value: 'active' },
                { label: `Inactive Only (${getStatsCount('inactive')})`, value: 'inactive' }
              ]}
            />
          </Col>
          <Col xs={24} md={6}>
            <Button
              block
              icon={<ReloadOutlined />}
              onClick={fetchPlugins}
            >
              Refresh List
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Plugin List */}
      {loading ? (
        <div>
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} style={{ marginBottom: 16 }}>
              <Skeleton active />
            </Card>
          ))}
        </div>
      ) : filteredPlugins.length === 0 ? (
        <Card>
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <Title level={5} type="secondary">
              {searchQuery ? '🔍 No apps found' : '📦 No apps installed'}
            </Title>
            <Text type="secondary">
              {searchQuery
                ? `No apps match "${searchQuery}". Try a different search term.`
                : 'You haven\'t installed any apps yet. Visit Apps Library to get started!'}
            </Text>
            {!searchQuery && (
              <div style={{ marginTop: 24 }}>
                <Button type="primary" size="large" icon={<span>🏪</span>}>
                  Browse Apps Library
                </Button>
              </div>
            )}
          </div>
        </Card>
      ) : (
        <div>
          {filteredPlugins.map((plugin) => (
            <Card
              key={plugin.id}
              style={{ marginBottom: 16 }}
              hoverable
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                {/* Plugin Icon */}
                <Avatar
                  size={64}
                  style={{
                    fontSize: '2rem',
                    backgroundColor: plugin.enabled ? '#e6f7ff' : '#f5f5f5',
                    color: plugin.enabled ? '#1890ff' : '#8c8c8c'
                  }}
                >
                  {plugin.icon}
                </Avatar>

                {/* Plugin Info */}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <Title level={5} style={{ margin: 0 }}>
                      {plugin.name}
                    </Title>
                    <Tag>{`v${plugin.version}`}</Tag>
                    <Tag color="blue">{plugin.category}</Tag>
                    <Tag color={plugin.enabled ? 'success' : 'default'}>
                      {plugin.enabled ? 'Active' : 'Inactive'}
                    </Tag>
                  </div>

                  <Paragraph type="secondary" style={{ marginBottom: 12 }}>
                    {plugin.description}
                  </Paragraph>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      👤 {plugin.author}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      📅 Installed {new Date(plugin.installedDate).toLocaleDateString()}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      🔄 Updated {new Date(plugin.lastUpdated).toLocaleDateString()}
                    </Text>
                    <Tooltip title={plugin.permissions.join(', ')}>
                      <Text type="secondary" style={{ fontSize: 12, cursor: 'help' }}>
                        🔐 {plugin.permissions.length} permission{plugin.permissions.length !== 1 ? 's' : ''}
                      </Text>
                    </Tooltip>
                  </div>
                </div>

                {/* Actions */}
                <Space>
                  {pluginRoutes.has(plugin.id) && (
                    <Tooltip title="Open plugin">
                      <Button
                        type="primary"
                        icon={<RocketOutlined />}
                        onClick={() => handleOpenPlugin(plugin)}
                        disabled={!plugin.enabled}
                      >
                        View
                      </Button>
                    </Tooltip>
                  )}
                  <Tooltip title={plugin.enabled ? 'Disable plugin' : 'Enable plugin'}>
                    <Switch
                      checked={plugin.enabled}
                      onChange={() => handleTogglePlugin(plugin.id, plugin.enabled)}
                    />
                  </Tooltip>
                  <Dropdown
                    menu={{ items: getMenuItems(plugin) }}
                    trigger={['click']}
                  >
                    <Button type="text" icon={<MoreOutlined />} />
                  </Dropdown>
                </Space>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Settings Dialog */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Avatar style={{ fontSize: '1.5rem' }}>{selectedPlugin?.icon}</Avatar>
            <div>
              <div>{selectedPlugin?.name} Settings</div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                v{selectedPlugin?.version}
              </Text>
            </div>
          </div>
        }
        open={settingsOpen}
        onOk={handleSettingsClose}
        onCancel={handleSettingsClose}
        width={600}
      >
        <Alert
          message="Plugin-specific settings will be loaded here. This is a placeholder for demonstration."
          type="info"
          style={{ marginBottom: 16 }}
        />
        <Text type="secondary">
          Settings interface will be dynamically loaded based on the plugin's configuration schema.
        </Text>
      </Modal>

      {/* Uninstall Confirmation Dialog */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '1.5rem' }}>⚠️</span>
            <span>Uninstall Plugin?</span>
          </div>
        }
        open={uninstallDialogOpen}
        onOk={handleUninstallConfirm}
        onCancel={handleUninstallCancel}
        okText="Uninstall"
        okButtonProps={{ danger: true }}
      >
        <Alert
          message="This action cannot be undone!"
          type="warning"
          style={{ marginBottom: 16 }}
        />
        <Paragraph>
          Are you sure you want to uninstall <Text strong>{pluginToUninstall?.name}</Text>?
        </Paragraph>
        <Paragraph type="secondary">
          All plugin data and configurations will be permanently removed. You can reinstall the plugin
          from the Marketplace later if needed.
        </Paragraph>
      </Modal>

      {/* Detailed Plugin View */}
      <DetailedPluginView
        pluginId={selectedPluginId}
        visible={detailedViewVisible}
        onClose={() => {
          setDetailedViewVisible(false);
          setSelectedPluginId(null);
        }}
      />
    </div>
  );
};

export default InstalledTab;

